import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useMemo, useState } from "react";
import { addEditorSource, decideSource, listSourceReview, type SourceRelation, type SourceRow } from "@/lib/post-sources.functions";

export const Route = createFileRoute("/admin/kaynaklar")({
  head: () => ({ meta: [{ title: "Kaynaklar · Yönetim" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: SourceReview,
});

const ORIGIN: Record<SourceRow["origin"], string> = {
  legacy: "Eski liste",
  report_match: "Rapor eşleştirme",
  ai_suggestion: "Otomatik öneri",
  editor: "Editör ekledi",
};
const RELATION: Record<SourceRelation, string> = { original: "Özgün kaynak", later_addition: "Sonradan eklenen kaynak" };
const STATUS: Record<SourceRow["status"], string> = { suggested: "Önerilen / doğrulanmamış", approved: "Onaylandı", rejected: "Reddedildi" };

function fmt(d: string | null) {
  return d ? new Date(d).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" }) : "—";
}

function SourceReview() {
  const list = useServerFn(listSourceReview);
  const q = useQuery({ queryKey: ["admin", "post-sources"], queryFn: () => list() });
  const [filter, setFilter] = useState<"pending" | "all">("pending");
  const [open, setOpen] = useState<string | null>(null);

  const data = q.data;
  const byPost = useMemo(() => {
    const m = new Map<string, SourceRow[]>();
    for (const s of data?.sources ?? []) m.set(s.post_id, [...(m.get(s.post_id) ?? []), s]);
    return m;
  }, [data]);

  if (q.isLoading) return <p className="p-8 text-sm text-muted-foreground">Yükleniyor…</p>;
  if (q.error || !data) return <p className="p-8 text-sm text-destructive">Kaynaklar yüklenemedi.</p>;

  const all = data.sources;
  const suggested = all.filter((s) => s.status === "suggested").length;
  const approved = all.filter((s) => s.status === "approved").length;
  const rejected = all.filter((s) => s.status === "rejected").length;
  const later = all.filter((s) => s.status === "suggested" && s.later_than_post).length;
  const pendingPosts = data.posts.filter((p) => (byPost.get(p.id) ?? []).some((s) => s.status === "suggested"));
  const shown = filter === "pending" ? pendingPosts : data.posts;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-8">
      <h1 className="font-serif text-3xl">Kaynaklar</h1>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        Yazı sayfasında yalnız sizin onayladığınız kaynaklar yayımlanır. Önerilen kaynaklar yazı metnine ve sayfaya yansımaz.
        Onay için kaynağın metindeki hangi iddiayı desteklediğini yazmanız gerekir. Toplu onay yoktur.
      </p>

      <dl className="mt-6 grid grid-cols-2 gap-px border border-border bg-border sm:grid-cols-5">
        {[
          ["Doğrulanmamış", suggested],
          ["Onaylanmış", approved],
          ["Reddedilmiş", rejected],
          ["Yazıdan sonra yayımlanmış", later],
          ["İnceleme bekleyen yazı", pendingPosts.length],
        ].map(([k, v]) => (
          <div key={k as string} className="bg-background p-4">
            <dt className="text-xs text-muted-foreground">{k}</dt>
            <dd className="mt-1 font-serif text-2xl">{v}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-6 flex gap-4 text-sm" role="tablist">
        <button role="tab" aria-selected={filter === "pending"} onClick={() => setFilter("pending")} className={filter === "pending" ? "border-b border-foreground" : "text-muted-foreground"}>
          İnceleme bekleyen ({pendingPosts.length})
        </button>
        <button role="tab" aria-selected={filter === "all"} onClick={() => setFilter("all")} className={filter === "all" ? "border-b border-foreground" : "text-muted-foreground"}>
          Tüm yazılar ({data.posts.length})
        </button>
      </div>

      <ul className="mt-4 divide-y divide-border border-y border-border">
        {shown.map((p) => {
          const rows = byPost.get(p.id) ?? [];
          const c = (st: SourceRow["status"]) => rows.filter((r) => r.status === st).length;
          const isOpen = open === p.id;
          return (
            <li key={p.id}>
              <button className="flex w-full flex-wrap items-baseline gap-x-4 gap-y-1 py-3 text-left" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : p.id)}>
                <span className="font-serif text-lg">{p.title_tr}</span>
                <span className="text-xs text-muted-foreground">{fmt(p.published_at)}</span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {c("suggested")} bekliyor · {c("approved")} onaylı · {c("rejected")} reddedildi
                </span>
              </button>
              {isOpen && (
                <div className="space-y-3 pb-6">
                  {rows.map((r) => <SourceCard key={r.id} row={r} />)}
                  <AddSource postId={p.id} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function SourceCard({ row }: { row: SourceRow }) {
  const decide = useServerFn(decideSource);
  const qc = useQueryClient();
  const [title, setTitle] = useState(row.title);
  const [publisher, setPublisher] = useState(row.publisher ?? "");
  const [date, setDate] = useState(row.source_date ?? "");
  const [claim, setClaim] = useState(row.claim ?? "");
  const [relation, setRelation] = useState<SourceRelation>(row.later_than_post ? "later_addition" : row.relation);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function run(status: SourceRow["status"]) {
    setBusy(true);
    setErr(null);
    try {
      await decide({ data: { id: row.id, status, title, publisher: publisher || null, date: date || null, claim: claim || null, relation } });
      await qc.invalidateQueries({ queryKey: ["admin", "post-sources"] });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Kaydedilemedi");
    } finally {
      setBusy(false);
    }
  }

  const input = "w-full border border-input bg-background px-2 py-1.5 text-sm";
  return (
    <div className="border border-border p-4 text-sm">
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
        <span className="border border-border px-1.5 py-0.5">{STATUS[row.status]}</span>
        <span className="border border-border px-1.5 py-0.5">{RELATION[row.relation]}</span>
        <span className="text-muted-foreground">{ORIGIN[row.origin]}</span>
        {row.relation === "later_addition" && row.status === "approved" && row.decided_at && <span className="text-muted-foreground">Eklenme: {fmt(row.decided_at)}</span>}
        {row.later_than_post && (
          <span className="border border-destructive/50 px-1.5 py-0.5 text-destructive">Yazıdan sonra yayımlanmış</span>
        )}
        <a href={row.url} target="_blank" rel="noopener noreferrer" className="ml-auto underline underline-offset-2">Kaynağı aç</a>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="sm:col-span-2"><span className="text-xs text-muted-foreground">Başlık</span><input className={input} value={title} onChange={(e) => setTitle(e.target.value)} /></label>
        <label><span className="text-xs text-muted-foreground">Kurum / yayın</span><input className={input} value={publisher} onChange={(e) => setPublisher(e.target.value)} /></label>
        <label><span className="text-xs text-muted-foreground">Doğrulanmış tarih (YYYY, YYYY-AA veya YYYY-AA-GG)</span><input className={input} value={date} onChange={(e) => setDate(e.target.value)} placeholder="Bilinmiyorsa boş bırakın" /></label>
        <p className="break-all text-xs text-muted-foreground sm:col-span-2">{row.url}</p>
        <label className="sm:col-span-2"><span className="text-xs text-muted-foreground">Metindeki hangi iddiayı destekliyor? (onay için gerekli)</span><textarea className={input} rows={2} value={claim} onChange={(e) => setClaim(e.target.value)} /></label>
        <RelationPicker value={relation} onChange={setRelation} laterThanPost={row.later_than_post} />
      </div>
      {err && <p role="alert" className="mt-2 text-xs text-destructive">{err}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        {row.status !== "approved" && <button disabled={busy} onClick={() => run("approved")} className="border border-foreground px-3 py-1.5">Onayla ve yayımla</button>}
        {row.status !== "rejected" && <button disabled={busy} onClick={() => run("rejected")} className="border border-border px-3 py-1.5">Reddet</button>}
        {row.status !== "suggested" && <button disabled={busy} onClick={() => run("suggested")} className="px-3 py-1.5 text-muted-foreground">Öneriye geri al</button>}
      </div>
    </div>
  );
}

function AddSource({ postId }: { postId: string }) {
  const add = useServerFn(addEditorSource);
  const qc = useQueryClient();
  const [f, setF] = useState({ title: "", publisher: "", url: "", date: "", claim: "" });
  const [relation, setRelation] = useState<SourceRelation>("original");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const input = "w-full border border-input bg-background px-2 py-1.5 text-sm";
  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await add({ data: { postId, title: f.title, publisher: f.publisher || null, url: f.url, date: f.date || null, claim: f.claim, relation } });
      setF({ title: "", publisher: "", url: "", date: "", claim: "" });
      await qc.invalidateQueries({ queryKey: ["admin", "post-sources"] });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Eklenemedi");
    } finally {
      setBusy(false);
    }
  }
  return (
    <details className="border border-dashed border-border p-4 text-sm">
      <summary className="cursor-pointer">+ Kaynak ekle</summary>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <label className="sm:col-span-2"><span className="text-xs text-muted-foreground">Başlık</span><input className={input} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></label>
        <label><span className="text-xs text-muted-foreground">Kurum / yayın</span><input className={input} value={f.publisher} onChange={(e) => setF({ ...f, publisher: e.target.value })} /></label>
        <label><span className="text-xs text-muted-foreground">Tarih (isteğe bağlı)</span><input className={input} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} placeholder="2024 veya 2024-10-16" /></label>
        <label className="sm:col-span-2"><span className="text-xs text-muted-foreground">Adres (URL)</span><input className={input} value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} /></label>
        <label className="sm:col-span-2"><span className="text-xs text-muted-foreground">Metindeki hangi iddiayı destekliyor?</span><textarea className={input} rows={2} value={f.claim} onChange={(e) => setF({ ...f, claim: e.target.value })} /></label>
        <RelationPicker value={relation} onChange={setRelation} laterThanPost={false} />
      </div>
      {err && <p role="alert" className="mt-2 text-xs text-destructive">{err}</p>}
      <button disabled={busy} onClick={submit} className="mt-3 border border-foreground px-3 py-1.5">Ekle ve yayımla</button>
    </details>
  );
}

function RelationPicker({ value, onChange, laterThanPost }: { value: SourceRelation; onChange: (v: SourceRelation) => void; laterThanPost: boolean }) {
  const name = useId();
  return (
    <fieldset className="text-xs sm:col-span-2">
      <legend className="text-muted-foreground">Kaynağın yazıyla ilişkisi</legend>
      <label className={`mt-1 flex items-start gap-2 ${laterThanPost ? "opacity-50" : ""}`}>
        <input type="radio" name={name} checked={value === "original"} disabled={laterThanPost} onChange={() => onChange("original")} className="mt-0.5" />
        <span><strong>Özgün kaynak</strong> — yazı hazırlanırken mevcuttu ve gerçekten kullanıldı. "Kaynaklar" listesinde ve atıf (citation) olarak görünür.
          {laterThanPost && <span className="block text-destructive">Bu kaynak yazıdan sonra yayımlandığı için özgün kaynak olamaz.</span>}</span>
      </label>
      <label className="mt-1 flex items-start gap-2">
        <input type="radio" name={name} checked={value === "later_addition"} onChange={() => onChange("later_addition")} className="mt-0.5" />
        <span><strong>Sonradan eklenen kaynak</strong> — yazının yayımından sonra çıkan ilgili araştırma/bilgi. Ayrı bölümde, onay (eklenme) tarihiyle gösterilir; atıf sayılmaz.</span>
      </label>
    </fieldset>
  );
}
